import { keywordsMeta } from "@/lib/seo/keywords";
import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { HeroSignature } from "@/components/site/wow/HeroSignature";
import { TrustBand } from "@/components/site/TrustBand";
import { ProofStrip } from "@/components/site/ProofStrip";
import { Reveal } from "@/components/site/wow/Reveal";
import { ChooseYourPath } from "@/components/site/landing/ChooseYourPath";
import { AnswerFaq } from "@/components/site/aeo/AnswerFaq";
import { toFaqJsonLd } from "@/lib/aeo/answers";
import { breadcrumbs, aeoGeo, siteServiceGraph } from "@/lib/seo/schema";

// Frågorna som renderas synligt på startsidan — samma id:n används för
// FAQPage-schemat så DOM och strukturerad data alltid matchar.
const HOME_FAQ_IDS = [
  "sv_vad_ar_fenny",
  "sv_vad_ar_film_pa_abonnemang",
  "sv_vad_kostar_filmproduktion",
  "sv_hur_snabbt_producerar_fenny",
  "sv_skillnaden_pulse_prime",
  "sv_vad_ingar_i_filmabonnemang",
  "sv_vilka_foretag_passar_fenny",
  "sv_hur_bestaller_man_film",
];

// Lazy-loaded below-the-fold sections — håller huvudchunken liten utan
// att påverka LCP. Suspense-fallback har min-h så vi inte får layout-shift
// när sektionen byts in.
const ServiceTriad = lazy(() => import("@/components/site/wow/ServiceTriad").then(m => ({ default: m.ServiceTriad })));
const CaseReel = lazy(() => import("@/components/site/wow/CaseReel").then(m => ({ default: m.CaseReel })));
const PulseVsPrime = lazy(() => import("@/components/site/PulseVsPrime").then(m => ({ default: m.PulseVsPrime })));
const FormatShowcase = lazy(() => import("@/components/site/FormatShowcase").then(m => ({ default: m.FormatShowcase })));
const ValueLedger = lazy(() => import("@/components/site/ValueLedger").then(m => ({ default: m.ValueLedger })));
const RiskRemoval = lazy(() => import("@/components/site/RiskRemoval").then(m => ({ default: m.RiskRemoval })));
const FennyManifesto = lazy(() => import("@/components/site/wow/FennyManifesto").then(m => ({ default: m.FennyManifesto })));
const SignatureCta = lazy(() => import("@/components/site/wow/SignatureCta").then(m => ({ default: m.SignatureCta })));

import { useScrollDepth } from "@/hooks/useScrollDepth";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      ...keywordsMeta("/"),
      { title: "Fenny — Film som driver affären, i marknadens takt" },
      {
        name: "description",
        content:
          "Premiumfilm på abonnemang. Strategi, produktion och leverans i samma rytm — fast månadspris, första klippet inom 10–14 dagar.",
      },
      { property: "og:title", content: "Fenny — Film som driver affären, i marknadens takt" },
      {
        property: "og:description",
        content:
          "Strategi, produktion och leverans i samma rytm. Fast månadspris. Första klippet inom 10–14 dagar.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Fenny — Film som driver affären" },
      {
        name: "twitter:description",
        content: "Premiumfilm på abonnemang. Boka ett kostnadsfritt strategimöte.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://fenny.se/" },
      // Poster preloaded ONLY on mobile — desktop plays the showreel <video> instead
      // and the <picture> element that consumes this avif is hidden via `md:hidden`.
      { rel: "preload", as: "image", href: "/video/fenny-showreel-poster.avif", type: "image/avif", fetchPriority: "high", media: "(max-width: 767px)" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/", name: "Fenny — Film som driver affären, i marknadens takt", description: "Premiumfilm på abonnemang. Strategi, produktion och leverans i samma rytm — fast månadspris, första klippet inom 10–14 dagar."})) },
      { type: "application/ld+json", children: JSON.stringify(toFaqJsonLd(HOME_FAQ_IDS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }])),
      },
      // Samlat Service-schema för filmverksamheten.
      { type: "application/ld+json", children: JSON.stringify(siteServiceGraph()) },
    ],
  }),
  component: Index,
});


function SectionFallback({ h = 480 }: { h?: number }) {
  return <div aria-hidden style={{ minHeight: h }} />;
}

function Index() {
  useScrollDepth("home");
  return (
    <>
      <HeroSignature />
      <TrustBand
        eyebrow="Filmproducent åt"
        title="Garageportexperten, Bolt, Huawei, Renova, Wimeck och fler."
        body="Vi gör film för varumärken som inte har råd att se halvdana ut — och inte tid att vänta veckor på första klippet."
        ctaLocation="home_trust"
        showBrands={false}
      />
      <Reveal><ProofStrip /></Reveal>
      <Reveal><ChooseYourPath /></Reveal>

      <Suspense fallback={<SectionFallback />}>
        <Reveal><ServiceTriad /></Reveal>
        <Reveal><CaseReel /></Reveal>
        <Reveal><PulseVsPrime /></Reveal>
        <Reveal><FormatShowcase /></Reveal>
        <Reveal><ValueLedger /></Reveal>
        <Reveal><RiskRemoval /></Reveal>
        <FennyManifesto />
        <AnswerFaq
          answerIds={HOME_FAQ_IDS}
          sectionId="faq"
          title="Vanliga frågor om film på abonnemang"
        />
        <SignatureCta tone="peach" />

      </Suspense>
    </>
  );
}
